import _ from 'lodash'
import { createAction, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import waitingRoomModel, { CreateWaitingRoomDto, UpdateWaitingRoomDto, WaitingRoom } from '@/core/models/waiting-room.model'
import { SearchRequestOptions, SearchWaitingRoomsQuery } from '@/core/types/request'

import { RootState } from '@/redux/rootReducer'

export const waitingRoomAdapter = createEntityAdapter({ selectId: (waitingRoom: WaitingRoom) => waitingRoom._id })

const waitingRoomSelector = waitingRoomAdapter.getSelectors((state: RootState) => state.waitingRoom.waitingRooms)

export const selectWaitingRooms = waitingRoomSelector.selectAll
export const selectWaitingRoomById = waitingRoomSelector.selectById
export const selectWaitingRoomSearchParams = (state: RootState) => state.waitingRoom.search.params
export const selectWaitingRoomSearchResult = (state: RootState) => state.waitingRoom.search.result
export const selectNewlyCreatedWaitingRoomId = (state: RootState) => state.waitingRoom.newlyCreatedItemId
export const selectDoctorStatuses = (state: RootState) => state.waitingRoom.doctorStatuses

export const selectWaitingRoomCreateRequestTracker = (state: RootState) => state.waitingRoom.requestTracker.create
export const selectWaitingRoomUpdateRequestTracker = (state: RootState) => state.waitingRoom.requestTracker.update
export const selectWaitingRoomListRequestTracker = (state: RootState) => state.waitingRoom.requestTracker.list
export const selectWaitingRoomDeleteRequestTracker = (state: RootState) => state.waitingRoom.requestTracker.delete
export const selectWaitingRoomAcceptConsultationRequestTracker = (state: RootState) => state.waitingRoom.requestTracker.acceptConsultation

// Doctor status types
export type DoctorStatus = {
  doctorId: string
  status: 'available' | 'in_consultation' | 'offline'
  consultationId?: string
  lastUpdated: number
}

const initialState = _.merge(waitingRoomModel.getInitialState(), {
  requestTracker: {
    acceptConsultation: { error: null, loading: false },
  },
  doctorStatuses: {} as Record<string, { status: 'available' | 'in_consultation' | 'offline', consultationId?: string, lastUpdated: number }>
})

const waitingRoomSlice = createSlice({
  name: waitingRoomModel.reducerName,
  initialState,
  reducers: {
    createWaitingRoom(state, action: PayloadAction<CreateWaitingRoomDto>) {
      state.requestTracker.create.loading = true
      state.newlyCreatedItemId = undefined
    },
    createWaitingRoomSuccess(state, action: PayloadAction<WaitingRoom>) {
      const waitingRoom = action.payload
      waitingRoomAdapter.setOne(state.waitingRooms, waitingRoom)
      state.newlyCreatedItemId = waitingRoom._id
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createWaitingRoomFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    listWaitingRooms: {
      reducer: (state, action: PayloadAction<{ query: SearchWaitingRoomsQuery; options?: SearchRequestOptions }>) => {
        state.search.params.query = action.payload.query
        state.search.params.options = action.payload.options
        state.requestTracker.list.loading = true
      },
      prepare: (query: SearchWaitingRoomsQuery, options?: SearchRequestOptions) => ({
        payload: {
          query,
          options,
        },
      }),
    },
    listWaitingRoomsSuccess(state, action: PayloadAction<{ items: WaitingRoom[]; totalCount: number }>) {
      const { items, totalCount } = action.payload
      waitingRoomAdapter.setAll(state.waitingRooms, items)
      state.search.result.totalCount = totalCount
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    listWaitingRoomsFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },
    getWaitingRoom(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getWaitingRoomSuccess(state, action: PayloadAction<WaitingRoom>) {
      waitingRoomAdapter.setOne(state.waitingRooms, action.payload)
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getWaitingRoomFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    updateWaitingRoom: {
      reducer: (state, action: PayloadAction<{ waitingRoomId: string; updateWaitingRoomDto: UpdateWaitingRoomDto }>) => {
        state.requestTracker.update.loading = true
      },
      prepare: (waitingRoomId: string, updateWaitingRoomDto: UpdateWaitingRoomDto) => ({
        payload: { waitingRoomId, updateWaitingRoomDto },
      }),
    },
    updateWaitingRoomSuccess(state, action: PayloadAction<WaitingRoom>) {
      waitingRoomAdapter.setOne(state.waitingRooms, action.payload)
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateWaitingRoomFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    deleteWaitingRoom(state, action: PayloadAction<string>) {
      state.requestTracker.delete.loading = true
    },
    deleteWaitingRoomSuccess(state, action: PayloadAction<string>) {
      waitingRoomAdapter.removeOne(state.waitingRooms, action.payload)
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    deleteWaitingRoomFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
    acceptConsultation(state, action: PayloadAction<{ consultationId: string; doctorId: string; waitingRoomId: string }>) {
      state.requestTracker.acceptConsultation.loading = true
    },
    acceptConsultationSuccess(state, action: PayloadAction<WaitingRoom>) {
      waitingRoomAdapter.setOne(state.waitingRooms, action.payload)
      state.requestTracker.acceptConsultation.error = null
      state.requestTracker.acceptConsultation.loading = false
    },
    acceptConsultationFailure(state, action) {
      state.requestTracker.acceptConsultation.error = action.payload
      state.requestTracker.acceptConsultation.loading = false
    },
    joinWaitingRoom(state, action: PayloadAction<string>) {
      state.requestTracker.update.loading = true
    },
    leaveWaitingRoom(state, action: PayloadAction<string>) {
      state.requestTracker.update.loading = true
    },
    // Doctor status reducers
    doctorStatusUpdated(state, action: PayloadAction<{ doctorId: string; status: 'available' | 'in_consultation' | 'offline'; consultationId?: string }>) {
      const { doctorId, status, consultationId } = action.payload;
      if (!state.doctorStatuses) {
        state.doctorStatuses = {};
      }

      if (status === 'offline') {
        // For offline status, remove the doctor from the statuses
        delete state.doctorStatuses[doctorId];
      } else {
        // For online statuses, update the status
        state.doctorStatuses[doctorId] = {
          status,
          consultationId,
          lastUpdated: Date.now()
        };
      }
    },

    waitingRoomDoctorsStatusReceived(state, action: PayloadAction<Array<{ doctorId: string; status: 'available' | 'in_consultation' | 'offline'; consultationId?: string }>>) {
      if (!state.doctorStatuses) {
        state.doctorStatuses = {};
      }

      const now = Date.now();

      action.payload.forEach((doctor) => {
        if (doctor.status === 'offline') {
          delete state.doctorStatuses[doctor.doctorId];
        } else {
          state.doctorStatuses[doctor.doctorId] = {
            status: doctor.status,
            consultationId: doctor.consultationId,
            lastUpdated: now
          };
        }
      });
    },

    clearDoctorStatuses(state) {
      state.doctorStatuses = {};
    }
  },
})

export const enterWaitingRoomSocketEvent = createAction<string>(`${waitingRoomModel.reducerName}/enterWaitingRoomSocketEvent`)
export const leaveWaitingRoomSocketEvent = createAction<string>(`${waitingRoomModel.reducerName}/leaveWaitingRoomSocketEvent`)
export const doctorStatusUpdatedSocketEvent = createAction<{
  doctorId: string;
  status: 'available' | 'in_consultation' | 'offline';
  consultationId?: string;
}>(`${waitingRoomModel.reducerName}/doctorStatusUpdatedSocketEvent`)
export const waitingRoomDoctorsStatusSocketEvent = createAction<Array<{
  doctorId: string;
  status: 'available' | 'in_consultation' | 'offline';
  consultationId?: string;
}>>(`${waitingRoomModel.reducerName}/waitingRoomDoctorsStatusSocketEvent`)
export const doctorHeartbeatSocketEvent = createAction<{
  waitingRoomId: string;
}>(`${waitingRoomModel.reducerName}/doctorHeartbeatSocketEvent`)

export const {
  createWaitingRoom,
  createWaitingRoomSuccess,
  createWaitingRoomFailure,
  listWaitingRooms,
  listWaitingRoomsSuccess,
  listWaitingRoomsFailure,
  getWaitingRoom,
  getWaitingRoomSuccess,
  getWaitingRoomFailure,
  updateWaitingRoom,
  updateWaitingRoomSuccess,
  updateWaitingRoomFailure,
  deleteWaitingRoom,
  deleteWaitingRoomSuccess,
  deleteWaitingRoomFailure,
  acceptConsultation,
  acceptConsultationSuccess,
  acceptConsultationFailure,
  joinWaitingRoom,
  leaveWaitingRoom,
  doctorStatusUpdated,
  waitingRoomDoctorsStatusReceived,
  clearDoctorStatuses
} = waitingRoomSlice.actions

export default waitingRoomSlice.reducer
